import { XMarkIcon } from '@heroicons/react/24/outline';
import { createContext, forwardRef, Fragment, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin, twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Colors, Sizes } from '@/components/ui/types';
import { preventDefault } from '@/utils/mouseEvent';
import { Button } from '../button';
import Fieldset from '../fieldset';
import { FloatingLabel, Label, LabelProps } from '../label';
import {
  TextInputAddonProps,
  TextInputIconProps,
  TextInputProps,
  TextInputVariant,
  TextInputWrapperProps,
} from './types';

type TextInputContextValue = {
  isInsideWrapper: boolean;
};

const TextInputContext = createContext<TextInputContextValue>({
  isInsideWrapper: false,
});

const useTextInputContext = () => useContext(TextInputContext);

const sizeClasses: Record<Sizes, string> = {
  xs: 'input-xs',
  sm: 'input-sm',
  md: 'input-md',
  lg: 'input-lg',
  xl: 'input-xl',
};

const colorClasses: Record<Colors, string> = {
  default: '',
  neutral: 'input-neutral',
  primary: 'input-primary',
  secondary: 'input-secondary',
  accent: 'input-accent',
  info: 'input-info',
  success: 'input-success',
  warning: 'input-warning',
  error: 'input-error',
};

const variantClasses: Record<TextInputVariant, string> = {
  default: '',
  ghost: 'input-ghost',
};

const TextInputBase = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      className,
      size = 'md',
      color = 'default',
      variant = 'default',
      error = false,
      onClear,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslation('translation', { keyPrefix: 'common' });
    const { isInsideWrapper } = useTextInputContext();

    const isSearch = props.type === 'search';
    const showClear =
      isSearch &&
      isInsideWrapper &&
      !!onClear &&
      !!props.value &&
      !props.disabled;

    const classes = twMerge(
      isInsideWrapper ? 'grow' : 'input',
      !isInsideWrapper && sizeClasses[size],
      !isInsideWrapper && colorClasses[color],
      !isInsideWrapper && variantClasses[variant],
      !isInsideWrapper && error && 'input-error',
      isSearch && '[&::-webkit-search-cancel-button]:hidden',
      className,
    );

    return (
      <Fragment>
        <input ref={ref} className={classes} {...props} />
        <ConditionalRenderer condition={showClear}>
          <Icon position="end">
            <Button
              aria-label={t('clear')}
              variant="ghost"
              size="xs"
              shape="circle"
              type="button"
              tabIndex={-1}
              onMouseDown={preventDefault}
              onClick={event => {
                preventDefault(event);
                onClear?.();
              }}
            >
              <XMarkIcon className="size-4 text-error" />
            </Button>
          </Icon>
        </ConditionalRenderer>
      </Fragment>
    );
  },
);

const Wrapper = ({
  className,
  children,
  size = 'md',
  color = 'default',
  variant = 'default',
  error = false,
  disabled = false,
  floatingLabel,
}: TextInputWrapperProps) => {
  const classes = twMerge(
    'input',
    sizeClasses[size],
    colorClasses[color],
    variantClasses[variant],
    error && 'input-error',
    disabled && 'input-disabled',
    className,
  );

  const content = <label className={classes}>{children}</label>;

  if (floatingLabel) {
    return (
      <TextInputContext.Provider value={{ isInsideWrapper: true }}>
        <FloatingLabel label={floatingLabel}>{content}</FloatingLabel>
      </TextInputContext.Provider>
    );
  }

  return (
    <TextInputContext.Provider value={{ isInsideWrapper: true }}>
      {content}
    </TextInputContext.Provider>
  );
};

const Icon = ({
  className,
  children,
  position = 'start',
}: TextInputIconProps) => {
  return (
    <span
      className={twJoin(
        position === 'start' ? 'order-first' : 'order-last',
        className,
      )}
    >
      {children}
    </span>
  );
};

const Addon = ({ className, children }: TextInputAddonProps) => {
  return <span className={className}>{children}</span>;
};

const TextInputLabel = (props: LabelProps<'span'>) => (
  <Label {...props} as="span" />
);

TextInputBase.displayName = 'TextInput';
Wrapper.displayName = 'TextInput.Wrapper';
Icon.displayName = 'TextInput.Icon';
Addon.displayName = 'TextInput.Addon';
TextInputLabel.displayName = 'TextInput.Label';

export const TextInput = Object.assign(TextInputBase, {
  Wrapper,
  Label: TextInputLabel,
  Icon,
  Addon,
  Fieldset,
});
